import { useState } from 'react';
import toast from 'react-hot-toast';

import { Button } from '@/components/ui';
import { Input, Select } from '@/components/forms';
import { useModalStore } from '@/stores';
import { useCreateUser } from '@/api/useUsers';

import styles from './CreateUserModal.module.css';

interface CreateUserModalProps {
  handleClose?: () => void;
}

const ROLE_OPTIONS = [
  { value: 'unassigned', label: 'Unassigned' },
  { value: 'provider', label: 'Provider' },
];

export function CreateUserModal({ handleClose }: CreateUserModalProps) {
  const { closeModal } = useModalStore();
  const createUser = useCreateUser();

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'unassigned' | 'provider'>('unassigned');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleDismiss = () => {
    if (handleClose) handleClose();
    else closeModal();
  };

  const validate = () => {
    const next: Record<string, string> = {};
    if (!email.trim()) next.email = 'Email is required';
    if (!password.trim()) next.password = 'Password is required';
    else if (password.length < 8) next.password = 'Password must be at least 8 characters';
    if (!role) next.role = 'Role is required';
    return next;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next = validate();
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});

    createUser.mutate(
      {
        email: email.trim(),
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
        password,
        role,
      },
      {
        onSuccess: () => {
          toast.success('User created successfully');
          handleDismiss();
        },
        onError: (error: unknown) => {
          const apiError = error as { response?: { data?: Record<string, string[]> } };
          const data = apiError.response?.data;
          if (data) {
            const fieldErrors: Record<string, string> = {};
            Object.entries(data).forEach(([key, msgs]) => {
              fieldErrors[key] = Array.isArray(msgs) ? msgs[0] : String(msgs);
            });
            setErrors(fieldErrors);
          }
          toast.error('Failed to create user');
        },
      },
    );
  };

  return (
    <div className={styles.container}>
      <h3 className={styles.title}>Create User</h3>

      <form className={styles.form} onSubmit={handleSubmit}>
        <Input
          name="email"
          label="Email"
          type="email"
          value={email}
          setValue={setEmail}
          placeholder="user@example.com"
          variant="bordered"
          isRequired
          errorMessage={errors.email}
        />

        <div className={styles.row}>
          <Input
            name="first_name"
            label="First Name"
            value={firstName}
            setValue={setFirstName}
            placeholder="First name"
            variant="bordered"
            errorMessage={errors.first_name}
          />
          <Input
            name="last_name"
            label="Last Name"
            value={lastName}
            setValue={setLastName}
            placeholder="Last name"
            variant="bordered"
            errorMessage={errors.last_name}
          />
        </div>

        <Input
          name="password"
          label="Password"
          type="password"
          value={password}
          setValue={setPassword}
          placeholder="Min. 8 characters"
          variant="bordered"
          isRequired
          minLength={8}
          errorMessage={errors.password}
        />

        <Select
          label="Role"
          value={role}
          onChange={(e) => setRole(e.target.value as 'unassigned' | 'provider')}
          options={ROLE_OPTIONS}
          required
          error={errors.role}
        />

        <div className={styles.actions}>
          <Button
            variant="secondary"
            type="button"
            onClick={handleDismiss}
            disabled={createUser.isPending}
          >
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={createUser.isPending}>
            {createUser.isPending ? 'Creating...' : 'Create User'}
          </Button>
        </div>
      </form>
    </div>
  );
}
